import { useState } from 'react';
import {
	Plus,
	Search,
	Pencil,
	Trash2,
	Box,
	AlertTriangle,
	ArrowUpDown,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button, PageTitle, SearchInput } from '@/components/ui';
import { CopyWrapper } from '@/components/ui/CopyWrapper';
import { ResourceConfigModal } from './ResourceConfigModal';
import { ServiceDisplayOrderModal } from './ServiceDisplayOrderModal';
import { useGetServices } from '@/services';
import { useRemoveService } from '@/services/mutations/service';
import { cn } from '@/lib/utils';
import { useSearchParams } from 'react-router-dom';

// Componente DeleteModal (conforme seu exemplo)
const DeleteModal = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
}: any) => {
	if (!isOpen) return null;
	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative w-full max-w-[400px] rounded-[2rem] bg-white p-8 text-center shadow-2xl duration-200">
				<div className="mb-4 flex justify-center text-rose-500">
					<div className="rounded-full bg-rose-50 p-4">
						<AlertTriangle size={32} />
					</div>
				</div>
				<h3 className="mb-2 text-xl font-black text-stone-900">
					{title}
				</h3>
				<p
					className="mb-8 text-sm font-medium leading-relaxed text-stone-500"
					dangerouslySetInnerHTML={{ __html: description }}
				/>
				<div className="flex flex-col gap-3">
					<Button
						text="Confirmar exclusão"
						className="w-full justify-center rounded-full border-none bg-rose-600 py-3 font-bold text-white shadow-md hover:bg-rose-700"
						onClick={onConfirm}
					/>
					<button
						onClick={onClose}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600"
					>
						Cancelar
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

const ResourceCard = ({
	resource,
	onEdit,
	onDelete,
}: {
	resource: Services.Service;
	onEdit: any;
	onDelete: any;
}) => {
	const formatDate = (dateStr: string) =>
		new Date(dateStr).toLocaleDateString('pt-BR');
	const shelf = (resource as { shelf?: Services.Shelf | null }).shelf;
	const shelfLabel =
		shelf === 'B2B2C'
			? 'B2B2C (Empresas)'
			: shelf === 'B2C'
			  ? 'B2C (Varejo)'
			  : null;

	return (
		<div className="group relative flex h-fit flex-col overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm transition-all hover:shadow-md">
			<div className="space-y-4 p-5">
				<div className="flex items-start justify-between">
					<div className="flex flex-wrap gap-2">
						<div
							className={cn(
								'flex items-center gap-1.5 rounded-md border px-2 py-1',
								resource.active
									? 'border-emerald-100 bg-emerald-50 text-emerald-600'
									: 'border-stone-200 bg-stone-50 text-stone-400',
							)}
						>
							<div
								className={cn(
									'size-1.5 rounded-full',
									resource.active
										? 'bg-emerald-500'
										: 'bg-stone-400',
								)}
							/>
							<span className="text-[9px] font-black uppercase tracking-widest">
								{resource.active ? 'Ativo' : 'Inativo'}
							</span>
						</div>
						{resource.type && (
							<div className="flex items-center rounded-md border border-violet-100 bg-violet-50 px-2 py-1 text-violet-600">
								<span className="text-[9px] font-black uppercase tracking-widest">
									{resource.type}
								</span>
							</div>
						)}
						{shelfLabel && (
							<div className="flex items-center rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-blue-600">
								<span className="text-[9px] font-black uppercase tracking-widest">
									{shelfLabel}
								</span>
							</div>
						)}
					</div>
					<div className="flex gap-1">
						<button
							onClick={() => onEdit(resource)}
							className="rounded-lg p-2 text-stone-400 hover:bg-stone-50 hover:text-stone-900"
						>
							<Pencil size={16} />
						</button>
						<button
							onClick={() => onDelete(resource)}
							className="rounded-lg p-2 text-stone-400 hover:bg-rose-50 hover:text-rose-600"
						>
							<Trash2 size={16} />
						</button>
					</div>
				</div>

				<div className="flex gap-4">
					<div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-stone-50 text-stone-400">
						{resource.icon_url ? (
							<img
								src={resource.icon_url}
								className="h-full w-full object-cover"
							/>
						) : (
							<Box size={24} />
						)}
					</div>
					<div>
						<h3 className="text-lg font-bold leading-tight text-stone-900">
							{resource.service_name}
						</h3>
						{resource.professional_label && (
							<p className="text-[10px] font-bold uppercase text-stone-400">
								{resource.professional_label}
							</p>
						)}
					</div>
				</div>

				<p className="line-clamp-2 h-[2.5rem] text-xs leading-relaxed text-stone-400">
					{resource.description || 'Sem descrição.'}
				</p>

				<div className="flex items-end justify-between border-t border-stone-50 pt-4">
					<div>
						<p className="mb-1 text-[9px] font-black uppercase leading-none tracking-widest text-stone-300">
							Preço Base
						</p>
						<p className="text-lg font-black text-stone-900">
							{resource.value_currency}{' '}
							{parseFloat(resource.value || '0').toLocaleString(
								'pt-BR',
								{
									minimumFractionDigits: 2,
								},
							)}
						</p>
					</div>
					<div className="text-right text-[9px] font-bold uppercase tracking-tighter text-stone-300">
						Atualizado em: {formatDate(resource.updated_at)}
					</div>
				</div>
			</div>
			<div className="mt-auto flex flex-col gap-2 border-t border-stone-100 bg-stone-50/50 px-5 py-4">
				<CopyWrapper
					valueToCopy={resource.id}
					wrapperClassName="flex w-full items-center pr-8"
					buttonClassName="!right-0"
				>
					<span className="block truncate text-[10px] font-black uppercase tracking-widest text-stone-900">
						ID: {resource.id}
					</span>
				</CopyWrapper>
			</div>
		</div>
	);
};

const ResourcesPage = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isDisplayOrderOpen, setIsDisplayOrderOpen] = useState(false);
	const [selectedResource, setSelectedResource] =
		useState<Services.Service | null>(null);

	const [searchParams] = useSearchParams();
	const search = searchParams.get('search') || '';

	const { data, isLoading, isError } = useGetServices();
	const { mutateAsync: removeService } = useRemoveService(() => {
		setIsDeleteModalOpen(false);
		setSelectedResource(null);
	});

	const services = Array.isArray(data) ? data : [];
	const filteredServices = services.filter((s: Services.Service) =>
		(s.service_name || '').toLowerCase().includes(search.toLowerCase()),
	);

	const handleDeleteConfirm = async () => {
		if (selectedResource?.id) {
			await removeService(selectedResource.id);
		}
	};

	if (isLoading)
		return (
			<div className="flex h-[400px] animate-pulse items-center justify-center text-sm font-black uppercase tracking-widest text-stone-400">
				Carregando recursos...
			</div>
		);
	if (isError)
		return (
			<div className="flex h-[400px] flex-col items-center justify-center gap-4">
				<AlertTriangle size={48} className="text-rose-500" />
				<p className="text-sm font-bold text-stone-600">
					Erro ao carregar os recursos.
				</p>
			</div>
		);

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="space-y-1">
				<PageTitle text="Serviço" />
				<p className="text-sm text-stone-400">
					Defina os serviços isolados disponíveis para composição de
					benefícios.
				</p>
			</div>

			<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="w-full sm:max-w-md">
					<SearchInput
						placeholder="Buscar recurso..."
						className="bg-inherit"
					/>
				</div>
				<div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
					<Button
						text="Ordenar exibição"
						title="Ordem em que os serviços aparecem para o usuário no agendamento e na home"
						className="w-full rounded-full border border-stone-200 bg-white px-6 py-2.5 font-bold text-stone-900 shadow-sm hover:bg-stone-50 sm:w-auto"
						startIcon={
							<ArrowUpDown className="size-5 stroke-[3]" />
						}
						onClick={() => setIsDisplayOrderOpen(true)}
					/>
					<Button
						text="Novo Recurso"
						className="w-full rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
						startIcon={
							<Plus className="size-5 stroke-[3] text-white" />
						}
						onClick={() => {
							setSelectedResource(null);
							setIsModalOpen(true);
						}}
					/>
				</div>
			</div>

			{filteredServices.length > 0 ? (
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
					{filteredServices.map((res: Services.Service) => (
						<ResourceCard
							key={res.id}
							resource={res}
							onEdit={(r: Services.Service) => {
								setSelectedResource(r);
								setIsModalOpen(true);
							}}
							onDelete={(r: Services.Service) => {
								setSelectedResource(r);
								setIsDeleteModalOpen(true);
							}}
						/>
					))}
				</div>
			) : (
				<div className="flex flex-col items-center justify-center rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 py-24 text-center">
					<div className="mb-4 rounded-full bg-white p-4 text-stone-300 shadow-sm">
						<Search size={32} />
					</div>
					<h3 className="text-base font-bold text-stone-900">
						Nenhum recurso encontrado
					</h3>
					<p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-stone-400">
						Não encontramos recursos com os critérios de busca ou
						ainda não existem recursos cadastrados.
					</p>
				</div>
			)}

			<ResourceConfigModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				resource={selectedResource}
			/>

			<ServiceDisplayOrderModal
				isOpen={isDisplayOrderOpen}
				onClose={() => setIsDisplayOrderOpen(false)}
			/>

			<DeleteModal
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				onConfirm={handleDeleteConfirm}
				title="Excluir Recurso"
				description={`Você tem certeza que deseja excluir o benefício <strong>${selectedResource?.service_name}</strong>?`}
			/>
		</section>
	);
};

export { ResourcesPage };
